import { ReactNode } from "react";
import clsx from "clsx";
import { Section, SectionHeading } from "../../components";
import "./ImagesDescription.scss";

export const IMAGES_DESCRIPTION_LAYOUTS = {
  // Layout FULL (1 colonna)
  FULL_1_1: "FULL_1_1",
  FULL_4_3: "FULL_4_3",
  FULL_16_9: "FULL_16_9",
  FULL_21_9: "FULL_21_9",
  FULL_32_9: "FULL_32_9",
  // Layout TWO_COL (2 colonne)
  TWO_COL_1_1: "TWO_COL_1_1",
  TWO_COL_4_3: "TWO_COL_4_3",
  TWO_COL_16_9: "TWO_COL_16_9",
  TWO_COL_21_9: "TWO_COL_21_9",
  TWO_COL_32_9: "TWO_COL_32_9",
  // Layout THREE_COL (3 colonne)
  THREE_COL_1_1: "THREE_COL_1_1",
  THREE_COL_4_3: "THREE_COL_4_3",
  THREE_COL_16_9: "THREE_COL_16_9",
  THREE_COL_21_9: "THREE_COL_21_9",
  THREE_COL_32_9: "THREE_COL_32_9",
} as const;

export type ImagesDescriptionLayout =
  (typeof IMAGES_DESCRIPTION_LAYOUTS)[keyof typeof IMAGES_DESCRIPTION_LAYOUTS];

export interface ImageDescriptionItem {
  _id: string;
  image: {
    src: string;
    alt: string;
    title?: string;
  };
  description: string;
}

export interface ImagesDescriptionProps {
  title: ReactNode;
  items: ImageDescriptionItem[];
  layout?: ImagesDescriptionLayout;
  className?: string;
}

export const ImagesDescription = ({
  title,
  items = [],
  layout = IMAGES_DESCRIPTION_LAYOUTS.FULL_1_1,
  className,
}: ImagesDescriptionProps) => {
  const getLayoutClassName = (l: ImagesDescriptionLayout) => {
    switch (l) {
      // Layout FULL (1 colonna)
      case IMAGES_DESCRIPTION_LAYOUTS.FULL_1_1:
        return "images-description-layout-full-1-1";
      case IMAGES_DESCRIPTION_LAYOUTS.FULL_4_3:
        return "images-description-layout-full-4-3";
      case IMAGES_DESCRIPTION_LAYOUTS.FULL_16_9:
        return "images-description-layout-full-16-9";
      case IMAGES_DESCRIPTION_LAYOUTS.FULL_21_9:
        return "images-description-layout-full-21-9";
      case IMAGES_DESCRIPTION_LAYOUTS.FULL_32_9:
        return "images-description-layout-full-32-9";

      // Layout TWO_COL (2 colonne)
      case IMAGES_DESCRIPTION_LAYOUTS.TWO_COL_1_1:
        return "images-description-layout-two-col-1-1";
      case IMAGES_DESCRIPTION_LAYOUTS.TWO_COL_4_3:
        return "images-description-layout-two-col-4-3";
      case IMAGES_DESCRIPTION_LAYOUTS.TWO_COL_16_9:
        return "images-description-layout-two-col-16-9";
      case IMAGES_DESCRIPTION_LAYOUTS.TWO_COL_21_9:
        return "images-description-layout-two-col-21-9";
      case IMAGES_DESCRIPTION_LAYOUTS.TWO_COL_32_9:
        return "images-description-layout-two-col-32-9";

      // Layout THREE_COL (3 colonne)
      case IMAGES_DESCRIPTION_LAYOUTS.THREE_COL_1_1:
        return "images-description-layout-three-col-1-1";
      case IMAGES_DESCRIPTION_LAYOUTS.THREE_COL_4_3:
        return "images-description-layout-three-col-4-3";
      case IMAGES_DESCRIPTION_LAYOUTS.THREE_COL_16_9:
        return "images-description-layout-three-col-16-9";
      case IMAGES_DESCRIPTION_LAYOUTS.THREE_COL_21_9:
        return "images-description-layout-three-col-21-9";
      case IMAGES_DESCRIPTION_LAYOUTS.THREE_COL_32_9:
        return "images-description-layout-three-col-32-9";

      default:
        return "";
    }
  };

  /* const isFullLayout = layout.startsWith("FULL_");
  const isThreeColLayout = layout.startsWith("THREE_COL_");

  const limitedItems = isFullLayout
    ? items.slice(0, 1)
    : isThreeColLayout
      ? items.slice(0, 3)
      : items.slice(0, 2); */

  const layoutClassName = getLayoutClassName(layout);

  return (
    <Section className={clsx("base-col images-description-section", className)}>
      <SectionHeading title={title} />
      <ul
        role="list"
        aria-label={`Raccolta di ${title}`}
        className={`list-style-none ${layoutClassName}`}
      >
        {items?.map((item) => (
          <li key={item._id} className="images-description-item">
            <div className="images-description-item-image-container">
              <img
                src={item.image.src}
                alt={item.image.alt}
                title={item.image.title}
                className="images-description-item-image"
              />
            </div>
            <div className="images-description-item-content">
              <p className="images-description-item-description text-slate-28 fs-9">
                {item.description}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
};
